import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchDetectedRegion, saveCustomerRegionPreference } from "@/lib/api";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";

const REGION_KEY = "mysitecare-region-preference";
const VALID_REGIONS = new Set(["eu", "ph"]);
const RegionContext = createContext({ region: "eu", source: "fallback", setRegionPreference: async () => {} });

const localPreference = () => {
  const value = window.localStorage.getItem(REGION_KEY);
  return VALID_REGIONS.has(value) ? value : null;
};

export function RegionProvider({ children }) {
  const { token, customer } = useCustomerAuth();
  const [manualRegion, setManualRegion] = useState(localPreference);
  const [edgeResolution, setEdgeResolution] = useState(null);
  const [accountRegion, setAccountRegion] = useState(null);
  const [pendingAccountRegion, setPendingAccountRegion] = useState(null);

  useEffect(() => {
    const saved = customer?.region_preference;
    setAccountRegion(VALID_REGIONS.has(saved) ? saved : null);
  }, [customer?.region_preference]);

  useEffect(() => {
    let cancelled = false;
    fetchDetectedRegion()
      .then((result) => {
        if (!cancelled && VALID_REGIONS.has(result.region)) setEdgeResolution(result);
      })
      .catch(() => {
        if (!cancelled) setEdgeResolution({ region: "eu", source: "fallback" });
      });
    return () => { cancelled = true; };
  }, []);

  const region = pendingAccountRegion || accountRegion || manualRegion || edgeResolution?.region || "eu";
  const source = pendingAccountRegion ? "manual" : accountRegion ? "account" : manualRegion ? "manual" : edgeResolution?.source || "fallback";

  const setRegionPreference = useCallback(async (nextRegion) => {
    if (!VALID_REGIONS.has(nextRegion)) return;
    window.localStorage.setItem(REGION_KEY, nextRegion);
    setManualRegion(nextRegion);
    if (token) {
      setPendingAccountRegion(nextRegion);
      try {
        await saveCustomerRegionPreference(token, nextRegion);
        setAccountRegion(nextRegion);
      } catch {
        // Keep a confirmed saved account preference authoritative on a save failure.
        if (accountRegion) {
          window.localStorage.setItem(REGION_KEY, accountRegion);
          setManualRegion(accountRegion);
        }
      } finally {
        setPendingAccountRegion(null);
      }
    }
  }, [accountRegion, token]);

  const value = useMemo(() => ({ region, source, setRegionPreference }), [region, source, setRegionPreference]);
  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
}

export function useRegionPreference() {
  return useContext(RegionContext);
}